import React, { useCallback, useEffect, useState } from "react";
import { api, errorMessage } from "../api/client";
import type { DemoSeed, DemoStatus } from "../api/types";
import { useSession } from "../store/session";
import { Button, Card, Field, Input, PageHeader } from "../components/Controls";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DEMO_EVENT } from "../components/Layout";
import { toast } from "../components/Toast";

function ChangeMyPin() {
  const { session } = useSession();
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setError(null);
    if (!/^\d{4,8}$/.test(pin)) return setError("El PIN debe tener entre 4 y 8 dígitos.");
    if (pin !== pin2) return setError("Los dos PIN no coinciden.");
    setBusy(true);
    try {
      await api.post(`/operators/${session.operator.id}/reset_pin`, { pin });
      toast.success("PIN actualizado");
      setPin("");
      setPin2("");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
    return undefined;
  };
  return (
    <Card className="p-5" data-testid="change-my-pin-block">
      <h3 className="mb-3 font-display text-base font-semibold">Cambiar mi PIN</h3>
      <div className="grid gap-3 sm:max-w-md sm:grid-cols-2">
        <Field label="Nuevo PIN" error={error}><Input type="password" inputMode="numeric" maxLength={8} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="my-pin-input" /></Field>
        <Field label="Repetir PIN"><Input type="password" inputMode="numeric" maxLength={8} value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))} data-testid="my-pin-confirm-input" /></Field>
      </div>
      <Button className="mt-3" onClick={save} loading={busy} data-testid="my-pin-save-button">Guardar</Button>
    </Card>
  );
}

function DemoBlock() {
  const [status, setStatus] = useState<DemoStatus | null>(null);
  const [seed, setSeed] = useState<DemoSeed | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const load = useCallback(() => api.get<DemoStatus>("/demo/status").then(setStatus).catch(() => undefined), []);
  useEffect(() => {
    load();
  }, [load]);
  const changed = () => {
    load();
    window.dispatchEvent(new Event(DEMO_EVENT));
  };
  const run = async (reset: boolean) => {
    setBusy(reset ? "reset" : "seed");
    try {
      const r = await api.post<DemoSeed>("/demo/seed", {}, reset ? { reset: "true" } : undefined);
      setSeed(r);
      toast.success("Datos demo cargados");
      changed();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  const c = status?.counts;
  return (
    <Card className="p-5" data-testid="demo-data-block">
      <h3 className="font-display text-base font-semibold">Datos demo</h3>
      <p className="mt-1 text-sm text-slate-500" data-testid="demo-status-counts">
        {c ? `${c.operators} operarios · ${c.parts} piezas · ${c.inspections} inspecciones de ejemplo` : "Cargando…"}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={() => run(false)} loading={busy === "seed"} disabled={!!status?.active} data-testid="demo-seed-button">Cargar datos demo</Button>
        <Button variant="secondary" onClick={() => setConfirmReset(true)} loading={busy === "reset"} data-testid="demo-reseed-button">Recargar</Button>
        <Button variant="danger" onClick={() => setConfirm(true)} disabled={!status?.active} data-testid="demo-clear-button">Borrar datos demo</Button>
      </div>
      {seed && (
        <div className="mt-4 overflow-x-auto" data-testid="demo-credentials-table">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500"><tr><th className="py-1 text-left">Nombre</th><th className="text-left">Usuario</th><th className="text-left">PIN</th><th className="text-left">Rol</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {seed.credentials.map((u) => (
                <tr key={u.username}><td className="py-1.5">{u.name}</td><td className="font-mono">{u.username}</td><td className="font-mono">{u.pin}</td><td>{u.role === "admin" ? "Administrador" : "Operario"}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog open={confirmReset} onClose={() => setConfirmReset(false)} title="Recargar datos demo" message="Se borran los datos demo actuales y se vuelven a generar desde cero." confirmLabel="Recargar" onConfirm={() => run(true)} />
      <ConfirmDialog open={confirm} onClose={() => setConfirm(false)} danger title="Borrar datos demo" message="Se borran todos los operarios, piezas, fotos e inspecciones marcados como demo. Los datos reales no se tocan." confirmLabel="Borrar" onConfirm={async () => { await api.del("/demo"); setSeed(null); toast.success("Datos demo borrados"); changed(); }} />
    </Card>
  );
}

export default function SettingsPage() {
  return (
    <div className="animate-rise space-y-5">
      <PageHeader title="Ajustes" />
      <ChangeMyPin />
      <Card className="p-5" data-testid="lens-install-block">
        <h3 className="font-display text-base font-semibold">Instalación en el lente</h3>
        <p className="mt-2 text-sm text-slate-400">Disponible en la próxima versión</p>
      </Card>
      <DemoBlock />
    </div>
  );
}
